import gql from 'graphql-tag';
import { set, get } from 'lodash';
import { format } from 'date-fns';
import { apolloClient } from '@/vue-apollo';
import querify from '@/utils/graphqlQuerify';

const priceSteps = new Array(11).fill(0).map((_, i) => i * 1000);
const priceRangeOptions = [
  ...priceSteps.map((x, i) => (i === priceSteps.length - 1
    ? `${x}+`
    : `${x} – ${priceSteps[i + 1]}`)),
];

function parsePricerRange(range) {
  if (range.includes('+')) {
    return {
      from: parseInt(range.replace('+', ''), 10),
      to: null,
    };
  }

  const bounds = range.split(' – ');
  return {
    from: parseInt(bounds[0], 10),
    to: parseInt(bounds[1], 10),
  };
}

function getPriceRange(price) {
  if (price == null) return 'Null';

  let i = 0;
  while (i <= (priceSteps.length - 1) && price >= priceSteps[i]) i += 1;

  return priceRangeOptions[i - 1];
}

function buildWhere(model, searchTerm, selectedCars) {
  const obj = {};
  let isFilter = false;
  const {
    start,
    end,
  } = model.carProductionDates;
  if (start && end) {
    const dateFilter = [
      {
        lighting_configurations: {
          car: {
            eop: { _gte: format(start, 'y-MM-dd'), _lte: format(end, 'y-MM-dd') },
          },
        },
      },
      {
        lighting_configurations: {
          car: {
            sop: { _lte: format(end, 'y-MM-dd'), _gte: format(start, 'y-MM-dd') },
          },
        },
      },
    ];
    set(obj, '_or', dateFilter);
  } else if (start) {
    set(obj, 'lighting_configurations.car.sop._gte', format(start, 'y-MM-dd'));
  } else if (end) {
    set(obj, 'lighting_configurations.car.eop._lte', format(end, 'y-MM-dd'));
  }

  if (model.carOem.length) {
    set(obj, 'lighting_configurations.car.brand.automaker.name._in', model.carOem);
  }

  if (model.carOemBrand.length) {
    set(obj, 'lighting_configurations.car.brand.name._in', model.carOemBrand);
  }

  if (model.carOemProductionRegion.length) {
    set(obj, 'lighting_configurations.car.country.region._in', model.carOemProductionRegion);
  }

  if (model.carOemProductionCountry.length) {
    set(obj, 'lighting_configurations.car.country.name._in', model.carOemProductionCountry);
  }

  if (model.carSegment.length) {
    set(obj, 'lighting_configurations.car.segment._in', model.carSegment);
  }

  if (model.carBody.length) {
    set(obj, 'lighting_configurations.car.body._in', model.carBody);
  }

  if (model.carModel.length) {
    set(obj, 'lighting_configurations.car.model._in', model.carModel);
  }

  if (model.carPowerTrain.length) {
    set(obj, 'lighting_configurations.car.power_train._in', model.carPowerTrain);
  }

  if (model.carGeneration.length) {
    set(obj, 'lighting_configurations.car.generation._in', model.carGeneration);
  }

  if (model.carPhase.length) {
    set(obj, 'lighting_configurations.car.phase._in', model.carPhase);
  }

  if (model.type.length) {
    set(obj, 'type._in', model.type);
  }

  if (model.supplier.length) {
    set(obj, 'supplier.name._in', model.supplier);
  }

  if (model.supplierProductionRegion.length) {
    set(obj, 'country.region._in', model.supplierProductionRegion);
  }

  if (model.supplierProductionCountry.length) {
    set(obj, 'country.name._in', model.supplierProductionCountry);
  }

  if (model.complexity.length) {
    set(obj, 'complexity._in', model.complexity);
  }

  if (model.aftermarket_price.length) {
    const ranges = model.aftermarket_price.map(parsePricerRange);
    set(obj, '_or', ranges.map((range) => ({
      aftermarket_price: {
        _gte: range.from,
        _lte: range.to,
      },
    })));
  }

  if (model.functionality.length) {
    set(obj, 'main_light_functions.light_functionality.name._in', model.functionality);
  }

  if (model.function.length) {
    set(obj, 'main_light_functions.light_function.name._in', model.function);
  }

  if (model.lightSourceTechnology.length) {
    set(obj, 'main_light_functions.light_source.technology._in', model.lightSourceTechnology);
  }

  if (model.opticalSystem.length) {
    set(obj, 'main_light_functions.optical_system.name._in', model.opticalSystem);
  }

  if (searchTerm) {
    const likeSearchTerm = `%${searchTerm.replaceAll(/"/g, '')}%`;
    set(obj, 'part_number._ilike', likeSearchTerm);
  }

  if (Object.entries(obj) !== 0) {
    isFilter = true;
  }

  set(obj, 'lighting_configurations.car.brand.validated._eq', true);
  set(obj, 'lighting_configurations.car.id._in', selectedCars);

  return {
    query: querify(obj),
    isFilter,
  };
}

async function getDynamicOptions(model = {}, searchTerm, changedField, selectedCars) {
  let where = buildWhere(model, searchTerm, selectedCars);
  if (!where.isFilter) return this.getOptions();
  where = where.query;
  const schemaOptions = [
    {
      name: 'carOem',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.brand.automaker.name',
    },
    {
      name: 'carOemBrand',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.brand.name',
    },
    {
      name: 'carOemProductionRegion',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.country.region',
    },
    {
      name: 'carOemProductionCountry',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.country.name',
    },
    {
      name: 'carSegment',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.segment',
    },
    {
      name: 'carBody',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.body',
    },
    {
      name: 'carModel',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.model',
    },
    {
      name: 'carPowerTrain',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.power_train',
    },
    {
      name: 'carGeneration',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.generation',
    },
    {
      name: 'carPhase',
      parentName: 'lightingConfigurations',
      parentType: 'Array',
      path: 'car.phase',
    },
    {
      name: 'type',
      parentName: 'root',
      parentType: 'Object',
      path: 'type',
    },
    {
      name: 'supplier',
      parentName: 'supplier',
      parentType: 'Object',
      path: 'supplier.name',
    },
    {
      name: 'supplierProductionRegion',
      parentName: 'country',
      parentType: 'Object',
      path: 'country.region',
    },
    {
      name: 'supplierProductionCountry',
      parentName: 'country',
      parentType: 'Object',
      path: 'country.name',
    },
    {
      name: 'complexity',
      parentName: 'root',
      parentType: 'Object',
      path: 'complexity',
    },
    {
      name: 'aftermarket_price',
      parentName: 'root',
      parentType: 'Object',
      path: 'aftermarket_price',
    },
    {
      name: 'functionality',
      parentName: 'mainLightFunctions',
      parentType: 'Array',
      path: 'light_functionality.name',
    },
    {
      name: 'function',
      parentName: 'mainLightFunctions',
      parentType: 'Array',
      path: 'light_function.name',
    },
    {
      name: 'lightSourceTechnology',
      parentName: 'mainLightFunctions',
      parentType: 'Array',
      path: 'light_source.technology',
    },
    {
      name: 'opticalSystem',
      parentName: 'mainLightFunctions',
      parentType: 'Array',
      path: 'optical_system.name',
    },
  ];

  const filteredData = await apolloClient.query({
    query: gql`query {
      
      lighting_devices(
        where: ${where},
      ) {
        id
        supplier {
          name
          logo
          region
          country
        }
        front_image
        type
        part_number
        aftermarket_price
        parts_per_car
        complexity
        mainLightFunctions: main_light_functions {
          light_function {
            name
          }
          light_source {
            technology
          }
          light_functionality {
            name
          }
          optical_system {
            name
          }
        }
        lightingConfigurations: lighting_configurations {
          car {
            brand {
              automaker {
                name
              }
              name
              logo
            }
            back_image
            front_image
            segment
            generation
            model
            body
            sop
            eop
            phase
            country {
              name
              region
            }
          }
          take_rates_aggregate {
            aggregate {
              sum {
                units
              }
            }
          }
        }
        country {
          name
          region
        }
      }
    }`,
  });

  const filteredDataInColumn = this.getModel();
  filteredData.data.lighting_devices.map((record) => {
    schemaOptions.map((item) => {
      if (item.parentType === 'Array') {
        const conditionObj = schemaOptions.filter((selector) => {
          if (selector.parentName === item.parentName) return true;
          return false;
        });

        record[item.parentName].map((nestedItem) => {
          const addedCondition = conditionObj.map((condition) => {
            const isEmpty = model[condition.name].length === 0;
            const hasItem = model[condition.name].includes(get(nestedItem, condition.path));
            return ((!isEmpty && hasItem) || isEmpty);
          });
          if (!addedCondition.includes(false)) {
            filteredDataInColumn[item.name].push(get(nestedItem, item.path));
          }
          return true;
        });
      }

      if (item.parentType === 'Object') {
        if (item.name === 'aftermarket_price') {
          filteredDataInColumn[item.name].push(getPriceRange(get(record, item.path)));
        } else {
          filteredDataInColumn[item.name].push(get(record, item.path));
        }
      }

      return true;
    });

    return true;
  });
  const result = {
    carOem: [...new Set(filteredDataInColumn.carOem)].sort(),
    carOemBrand: [...new Set(filteredDataInColumn.carOemBrand)].sort(),
    carOemProductionRegion: [...new Set(filteredDataInColumn.carOemProductionRegion)].sort(),
    carOemProductionCountry: [...new Set(filteredDataInColumn.carOemProductionCountry)].sort(),
    carSegment: [...new Set(filteredDataInColumn.carSegment)].sort(),
    carBody: [...new Set(filteredDataInColumn.carBody)].sort(),
    carModel: [...new Set(filteredDataInColumn.carModel)].sort(),
    carPowerTrain: [...new Set(filteredDataInColumn.carPowerTrain)].sort(),
    carGeneration: [...new Set(filteredDataInColumn.carGeneration)].sort(),
    carPhase: [...new Set(filteredDataInColumn.carPhase)].sort(),
    type: [...new Set(filteredDataInColumn.type)].sort(),
    supplier: [...new Set(filteredDataInColumn.supplier)].sort(),
    supplierProductionRegion: [...new Set(filteredDataInColumn.supplierProductionRegion)].sort(),
    supplierProductionCountry: [...new Set(filteredDataInColumn.supplierProductionCountry)].sort(),
    complexity: [...new Set(filteredDataInColumn.complexity)].sort(),
    aftermarket_price: [...new Set(filteredDataInColumn.aftermarket_price)].sort(),
    functionality: [...new Set(filteredDataInColumn.functionality)].sort(),
    function: [...new Set(filteredDataInColumn.function)].sort(),
    lightSourceTechnology: [...new Set(filteredDataInColumn.lightSourceTechnology)].sort(),
    opticalSystem: [...new Set(filteredDataInColumn.opticalSystem)].sort(),
  };

  if (changedField.field && changedField.direction === 'add') {
    delete result[changedField.field];
  }

  return result;
}

export default {
  getSchema() {
    return [
      {
        name: 'carProductionDates',
        type: 'dateRange',
        label: 'Production Dates',
        columnId: 'car',
      },
      {
        name: 'carOem',
        type: 'select',
        isMultiple: true,
        label: 'OEM',
        columnId: 'car',
      },
      {
        name: 'carOemBrand',
        type: 'select',
        isMultiple: true,
        label: 'OEM Brand',
        columnId: 'car',
      },
      {
        name: 'carSegment',
        type: 'select',
        isMultiple: true,
        label: 'Segment',
        columnId: 'car',
      },
      {
        name: 'carModel',
        type: 'select',
        isMultiple: true,
        label: 'Model',
        columnId: 'car',
      },
      {
        name: 'carPowerTrain',
        type: 'select',
        isMultiple: true,
        label: 'Power train',
        columnId: 'car',
      },
      {
        name: 'carGeneration',
        type: 'select',
        isMultiple: true,
        label: 'Generation',
        columnId: 'car',
      },
      {
        name: 'carPhase',
        type: 'select',
        isMultiple: true,
        label: 'Phase',
        columnId: 'car',
      },
      {
        name: 'carBody',
        type: 'select',
        isMultiple: true,
        label: 'Body',
        columnId: 'car',
      },
      {
        name: 'carOemProductionRegion',
        type: 'select',
        isMultiple: true,
        label: 'OEM production region',
        columnId: 'car',
      },
      {
        name: 'carOemProductionCountry',
        type: 'select',
        isMultiple: true,
        label: 'OEM production country',
        columnId: 'car',
      },
      {
        name: 'type',
        type: 'select',
        isMultiple: true,
        label: 'Type',
        columnId: 'lightingDevice',
      },
      {
        name: 'supplier',
        type: 'select',
        isMultiple: true,
        label: 'Supplier',
        columnId: 'lightingDevice',
      },
      {
        name: 'supplierProductionRegion',
        type: 'select',
        isMultiple: true,
        label: 'Supplier production region',
        columnId: 'lightingDevice',
      },
      {
        name: 'supplierProductionCountry',
        type: 'select',
        isMultiple: true,
        label: 'Supplier production country',
        columnId: 'lightingDevice',
      },
      {
        name: 'complexity',
        type: 'select',
        isMultiple: true,
        label: 'Complexity',
        columnId: 'lightingDevice',
      },
      {
        name: 'aftermarket_price',
        type: 'select',
        isMultiple: true,
        label: 'Aftermarket price',
        columnId: 'lightingDevice',
      },
      {
        name: 'functionality',
        type: 'select',
        isMultiple: true,
        label: 'Functionality',
        columnId: 'lightingDevice',
      },
      {
        name: 'function',
        type: 'select',
        isMultiple: true,
        label: 'Function',
        columnId: 'lightingDevice',
      },
      {
        name: 'lightSourceTechnology',
        type: 'select',
        isMultiple: true,
        label: 'Light source technology',
        columnId: 'lightingDevice',
      },
      {
        name: 'opticalSystem',
        type: 'select',
        isMultiple: true,
        label: 'Optical concept',
        columnId: 'lightingDevice',
      },
    ];
  },
  getModel() {
    return {
      carProductionDates: {
        start: null,
        end: null,
      },
      carOem: [],
      carOemBrand: [],
      carOemProductionRegion: [],
      carOemProductionCountry: [],
      carSegment: [],
      carBody: [],
      carModel: [],
      carPowerTrain: [],
      carGeneration: [],
      carPhase: [],
      type: [],
      supplier: [],
      supplierProductionRegion: [],
      supplierProductionCountry: [],
      complexity: [],
      aftermarket_price: [],
      functionality: [],
      function: [],
      lightSourceTechnology: [],
      opticalSystem: [],
    };
  },
  getDynamicOptions,
  async getOptions() {
    const result = await apolloClient.query({
      query: gql`query {
        carOem: automakers(
          where: { value_chain_level: { _eq: "OEM", }, },
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        carOemBrand: brands(
          where: { validated: { _eq: true, }, },
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        carOemProductionRegion: countries(
          distinct_on: region,
          order_by: {region: asc},
        ) {
          region
        }

        carOemProductionCountry: countries(
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        carSegment: cars(
          distinct_on: segment,
          order_by: {segment: asc},
        ) {
          segment
        }

        carBody: cars(
          distinct_on: body,
          order_by: {body: asc},
        ) {
          body
        }

        carModel: cars(
          distinct_on: model,
          order_by: {model: asc},
        ) {
          model
        }

        carPowerTrain: cars(
          distinct_on: power_train,
          order_by: {power_train: asc},
        ) {
          power_train
        }

        carGeneration: cars(
          distinct_on: generation,
          order_by: {generation: asc},
        ) {
          generation
        }

        carPhase: cars(
          distinct_on: phase,
          order_by: {phase: asc},
        ) {
          phase
        }

        supplier: brands(
          where: { value_chain_level: { _eq: "Tier1", }, },
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        supplierProductionRegion: countries(
          distinct_on: region,
          order_by: {region: asc},
        ) {
          region
        }

        supplierProductionCountry: countries(
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        complexity: lighting_devices(
          distinct_on: complexity,
          order_by: {complexity: asc},
        ) {
          complexity
        }

        type: lighting_devices(
          distinct_on: type,
          where: {type: {_nin: ["CHMSL","AuxHL","AuxRL"]}},
          order_by: {type: asc},
        ) {
          type
        }

        functionality: light_functionalities(
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        function: light_functions(
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }

        lightSourceTechnology: light_sources(
          distinct_on: technology,
          order_by: {technology: asc},
        ) {
          technology
        }

        opticalSystem: optical_systems(
          distinct_on: name,
          order_by: {name: asc},
        ) {
          name
        }
      }`,
    });

    return {
      carOem: result.data.carOem.map((x) => x.name),
      carOemBrand: result.data.carOemBrand.map((x) => x.name),
      carOemProductionRegion: result.data.carOemProductionRegion.map((x) => x.region),
      carOemProductionCountry: result.data.carOemProductionCountry.map((x) => x.name),
      carSegment: result.data.carSegment.map((x) => x.segment),
      carBody: result.data.carBody.map((x) => x.body),
      carModel: result.data.carModel.map((x) => x.model),
      carPowerTrain: result.data.carPowerTrain.map((x) => x.power_train),
      carGeneration: result.data.carGeneration.map((x) => x.generation),
      carPhase: result.data.carPhase.map((x) => x.phase),
      type: result.data.type.map((x) => x.type),
      supplier: result.data.supplier.map((x) => x.name),
      supplierProductionRegion: result.data.supplierProductionRegion.map((x) => x.region),
      supplierProductionCountry: result.data.supplierProductionCountry.map((x) => x.name),
      complexity: result.data.complexity.map((x) => x.complexity),
      aftermarket_price: priceRangeOptions,
      functionality: result.data.functionality.map((x) => x.name),
      function: result.data.function.map((x) => x.name),
      lightSourceTechnology: result.data.lightSourceTechnology.map((x) => x.technology),
      opticalSystem: result.data.opticalSystem.map((x) => x.name),
    };
  },
  async getData(model, searchTerm, perPage, page, companyUuid, selectedCars) {
    const where = buildWhere(model, searchTerm, selectedCars).query;
    const offset = (page - 1) * perPage;

    const res = await apolloClient.query({
      query: gql`query {
        lighting_devices_aggregate(
          where: ${where},
        ) {
          aggregate {
            count
          }
        }

        lighting_devices(
          where: ${where},
          limit: ${perPage},
          offset: ${offset},
        ) {
          id
          supplier {
            name
            logo
            region
            country
          }
          front_image
          type
          part_number
          aftermarket_price
          parts_per_car
          complexity
          lighting_configurations {
            car {
              brand {
                name
                logo
              }
              back_image
              front_image
              sop
              eop
              country {
                name
                region
              }
              volumes_infos {
                sop: sop_${companyUuid.replace(/-/g, '')}
                eop: eop_${companyUuid.replace(/-/g, '')}
                has_volumes: has_volumes_${companyUuid.replace(/-/g, '')}
              }
            }
            take_rates_aggregate {
              aggregate {
                sum {
                  units
                }
              }
            }
          }
          country {
            name
            region
          }
        }
      }`,
    });
    return {
      data: res.data.lighting_devices,
      total: res.data.lighting_devices_aggregate.aggregate.count,
    };
  },
};
